import { useMemo } from "react";

/**
 * Full-hero animated wall of game thumbnails.
 * 4 rows scroll horizontally at different speeds & directions; strong dark
 * overlay ensures the hero copy on top stays readable.
 */
export const GameMarqueeBackground = ({ games = [] }) => {
    // Build 4 rows out of the games list. If we have fewer than 4 games, just
    // duplicate them so every row has content.
    const rows = useMemo(() => {
        if (!games.length) return [[], [], [], []];
        // Shuffle deterministically-ish so rows aren't identical
        const shuffled = (offset) =>
            games.map((_, i) => games[(i + offset) % games.length]);
        return [shuffled(0), shuffled(2), shuffled(4), shuffled(1)];
    }, [games]);

    return (
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Base dark layer */}
            <div className="absolute inset-0 bg-[#000000]" />

            {/* Scrolling rows */}
            <div className="absolute inset-0 flex flex-col justify-center gap-4 py-4">
                {rows.map((row, ri) => (
                    <div
                        key={ri}
                        className={`marquee-track marquee-${ri}`}
                        style={{
                            animationDuration: `${60 + ri * 15}s`,
                            animationDirection: ri % 2 === 0 ? "normal" : "reverse",
                        }}
                    >
                        {/* Render twice for seamless loop */}
                        {[0, 1].map((dup) => (
                            <div key={dup} className="marquee-inner">
                                {row.map((g, i) => (
                                    <div
                                        key={`${ri}-${dup}-${i}-${g.placeId}`}
                                        className="marquee-tile"
                                    >
                                        {g.icon ? (
                                            <img
                                                src={g.icon}
                                                alt=""
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                                draggable={false}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-[#0F0A14]" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Overlays: mild darken + vignette + subtle blue tint for readability */}
            <div className="absolute inset-0 bg-black/30" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(6,11,25,0.1) 0%, rgba(6,11,25,0.7) 75%, #000000 100%)",
                }}
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(60% 50% at 50% 30%, rgba(20, 184, 166,0.15) 0%, transparent 65%)",
                }}
            />
        </div>
    );
};

export default GameMarqueeBackground;
